<template>
  <div class="accept-invite-page">
    <div class="accept-invite-page__main">
      <div class="accept-invite-page__wrap">
        <h2 class="accept-invite-page__title">Accept invite</h2>
        <form
          @submit.prevent="handleAcceptInvite"
          class="accept-invite-page__form mt-8"
          v-if="!isVerifyInviteLoading"
        >
          <TextInput
            :label="userData.email.label"
            v-model="userData.email.binding"
            type="email"
            disabled="true"
          />
          <TextInput
            :label="userData.password.label"
            v-model="userData.password.binding"
            type="password"
          />
          <hr />
          <TextInput
            :label="userData.first_name.label"
            v-model="userData.first_name.binding"
            type="text"
          />
          <TextInput
            :label="userData.last_name.label"
            v-model="userData.last_name.binding"
            type="text"
          />
          <p class="organization-info">Joining: {{ inviteData.organizationName }}</p>
          <Btn
            type="submit"
            :loading="isLoading"
            variant="secondary"
            icon="user"
            class="width-100 mt-2"
            text="Accept Invitation"
          />
        </form>
        <LoadingSpinner class="accept-invite-page__loading" size="xl" v-else />
      </div>
    </div>
    <div class="accept-invite-page__aside">
      <div class="accept-invite-page__aside__content">
        <img src="@/assets/img/auth-visual@2x.png" alt="FormFlow" />
      </div>
    </div>
  </div>
</template>

<script>
  import { useRouter, useRoute } from 'vue-router';
  import { ref, onMounted, getCurrentInstance } from 'vue';
  import { useUserStore } from '@/store/userStore';
  import { useConfetti } from '@/services/confettiService';
  import UserService from '@/services/userService';
  import axios from 'axios';
  import { supabase } from '@/services/supabase';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { useUIStore } from '@/store/uiStore';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  export default {
    components: {
      TextInput,
      Btn,
      LoadingSpinner,
    },
    setup() {
      const isLoading = ref(false);
      const isVerifyInviteLoading = ref(true);
      const confetti = useConfetti();
      const userData = ref({
        email: {
          label: 'Password',
          binding: '',
        },
        password: {
          label: 'Password',
          binding: '',
        },
        first_name: {
          label: 'First name',
          binding: '',
        },
        last_name: {
          label: 'Last name',
          binding: '',
        },
      });
      const router = useRouter();
      const route = useRoute();
      const $t = getCurrentInstance().appContext.config.globalProperties.$t;
      const userService = new UserService();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const inviteData = ref({
        token: '',
        email: '',
        organizationName: '',
      });

      onMounted(async () => {
        const token = route.query.token;

        if (!token) {
          router.push('/login');
          return;
        }

        try {
          const response = await userService.acceptInvite(token);
          console.log(response);
          isVerifyInviteLoading.value = false;
          if (response.isExistingUser) {
            // An existing invitee must be logged in AS the invited email to
            // accept. We only need a valid session here — NOT a full
            // "authenticated" state — because a user with no organizations can't
            // reach isAuthenticated, yet must still be able to accept an invite
            // (that's exactly how they get their first/next org). The backend
            // verifies the session's email matches the invite.
            const {
              data: { session },
            } = await supabase.auth.getSession();

            if (!session) {
              router.push({
                path: '/login',
                query: { redirect: `/accept-invite?token=${token}` },
              });
              return;
            }

            await handleExistingUserInvite(token);
          } else {
            // Setup form for new user
            inviteData.value = {
              token,
              email: response.invite.email,
              organizationName: response.invite.organization.name,
            };
            userData.value.email.binding = response.invite.email;
          }
        } catch (error) {
          // handleError(error);
        }
      });

      const handleAcceptInvite = async () => {
        isLoading.value = true;
        try {
          const response = await userService.acceptNewInvite(inviteData.value.token, {
            email: inviteData.value.email,
            password: userData.value.password.binding,
            first_name: userData.value.first_name.binding,
            last_name: userData.value.last_name.binding,
          });
          const loginRequest = await userStore.login(
            inviteData.value.email,
            userData.value.password.binding
          );
          confetti.fire();
          router.push('/all-flows');
        } catch (error) {
          console.log(error);
        } finally {
          isLoading.value = false;
        }
      };

      const handleExistingUserInvite = async (token) => {
        try {
          await axios.post('/users/accept-invite/existing', { token });
        } catch (error) {
          const isMismatch = error.response?.data?.code === 'EMAIL_MISMATCH';
          uiStore.addModal(null, {
            title: $t('auth.accept_invite_failed_title'),
            description: isMismatch
              ? $t('auth.accept_invite_email_mismatch')
              : $t('auth.accept_invite_failed_description'),
          });
          return;
        }

        // The invite is accepted server-side now. A failing init() must NOT be
        // reported as a failed acceptance — refresh best-effort, then continue.
        try {
          await userStore.init();
        } catch (initError) {
          console.error('Failed to refresh user store after accepting invite:', initError);
        }

        uiStore.addModal(null, {
          title: $t('auth.accept_invite_success_title'),
          description: $t('auth.accept_invite_success_description'),
        });
        router.push('/all-flows');
      };

      return {
        isLoading,
        userData,
        inviteData,
        isVerifyInviteLoading,
        handleAcceptInvite,
      };
    },
  };
</script>
<style scoped lang="scss">
  .accept-invite-page {
    display: flex;
    min-height: 100vh;
    @include lg-down {
      flex-direction: column;
    }
    &__main {
      width: 50%;
      align-items: center;
      justify-content: center;
      display: flex;
      min-height: 100%;
      background-color: #ffffff;
      p a {
        text-decoration: underline;
      }
      @include lg-down {
        width: 100%;
        min-height: 100vh;
      }
    }
    &__form {
      max-width: 100%;
      width: 400px;
    }
    &__aside {
      width: 50%;
      min-height: 100%;
      background-color: #092220;
      background: linear-gradient(49.61deg, #092220 46.86%, #1b4743 101.41%);
      padding: 8rem;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 100%;
        max-width: 400px;
      }
      @include lg-down {
        width: 100%;
      }
    }
    &__loading {
      margin: 1rem auto;
    }
  }
</style>
